import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { apiBaseUrl } from '../../util/api';
import { MAX_CART_ROWS, cartItemCount } from '../../util/cart';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import { createSlug } from '../../util/urlHelpers';
import { useShippingDestination } from '../../util/shippingDestination';
import { getWatchedListingEntries } from '../../util/watchedListings';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import {
  cartMergeSummarySelector,
  cartRowsSelector,
  clearMergeSummary,
  removeFromCart,
  updateCartRowDeliveryMethod,
  updateCartRowQty,
} from '../../ducks/cart.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { H3, InlineTextButton, LayoutSingleColumn, NamedLink, Page } from '../../components';
import ShippingDestinationSelect from '../../components/ShippingDestinationSelect/ShippingDestinationSelect';

import TopbarContainer from '../TopbarContainer/TopbarContainer';

import CartBoxCard from './CartBoxCard';
import CartRow from './CartRow';
import { fetchListingForCheckout, revalidateCart } from './CartPage.duck';
import { isCartEmpty, mergeSummaryHasContent, partitionCartRows } from './CartPage.shared';
import { prepareBoxCheckout } from './cartCheckout';
import css from './CartPage.module.css';

// Revalidation runs on mount and on window focus after this long, so a cart left open in
// a tab cannot quietly go stale.
const REFOCUS_REVALIDATE_MS = 60 * 1000;

const CART_PATH = '/cart';

/** A row identity that changes only when something worth re-quoting changed. */
const cartRowsFingerprint = rows =>
  (Array.isArray(rows) ? rows : [])
    .map(r => `${r.listingId}:${r.qty}:${r.deliveryMethod}`)
    .join('|');

const MergeNotice = ({ summary, onDismiss }) => {
  const { carriedOver = 0, conflicts = [], droppedForCap = 0 } = summary;
  return (
    <div className={css.mergeNotice} role="status">
      <div className={css.mergeNoticeBody}>
        {carriedOver > 0 ? (
          <p className={css.mergeNoticeLine}>
            <FormattedMessage id="CartPage.mergeCarriedOver" values={{ count: carriedOver }} />
          </p>
        ) : null}
        {conflicts.length > 0 ? (
          <p className={css.mergeNoticeLine}>
            <FormattedMessage id="CartPage.mergeConflicts" values={{ count: conflicts.length }} />
          </p>
        ) : null}
        {droppedForCap > 0 ? (
          <p className={css.mergeNoticeLine}>
            <FormattedMessage
              id="CartPage.mergeDroppedForCap"
              values={{ count: droppedForCap, max: MAX_CART_ROWS }}
            />
          </p>
        ) : null}
      </div>
      <InlineTextButton className={css.mergeNoticeDismiss} type="button" onClick={onDismiss}>
        <FormattedMessage id="CartPage.mergeNoticeDismiss" />
      </InlineTextButton>
    </div>
  );
};

const EmptyCart = ({ watchedCount }) => (
  <div className={css.emptyState}>
    <H3 as="h1" className={css.emptyHeading}>
      <FormattedMessage id="CartPage.emptyHeading" />
    </H3>
    <p className={css.emptyBody}>
      <FormattedMessage id="CartPage.emptyBody" />
    </p>
    <NamedLink className={css.emptyBrowseLink} name="SearchPage">
      <FormattedMessage id="CartPage.emptyBrowseListings" />
    </NamedLink>
    {watchedCount > 0 ? (
      <p className={css.watchlistBridge}>
        <NamedLink name="WatchedListingsPage">
          <FormattedMessage id="CartPage.watchlistBridge" values={{ count: watchedCount }} />
        </NamedLink>
      </p>
    ) : null}
  </div>
);

/**
 * The Cart page: boxes, ledgers and every stale state the server reports.
 *
 * @component
 */
export const CartPageComponent = props => {
  const {
    currentUser,
    cartRows,
    cartHydrated,
    rows,
    groups,
    loaded,
    revalidateInProgress,
    revalidateError,
    mergeSummary,
    updateInProgress,
    updatingListingId,
    scrollingDisabled,
    onRevalidateCart,
    onRemoveFromCart,
    onUpdateCartRowQty,
    onUpdateCartRowDeliveryMethod,
    onClearMergeSummary,
  } = props;
  const intl = useIntl();
  const history = useHistory();
  const routes = useRouteConfiguration();
  const config = useConfiguration();
  const dispatch = useDispatch();
  const [destination, setDestination, destinationHydrated] = useShippingDestination();
  const destKey = destination ? `${destination.country || ''}:${destination.state || ''}` : '';
  const revalidateWithDestination = useCallback(
    rows => (destination ? onRevalidateCart(rows, { destination }) : onRevalidateCart(rows)),
    [destination, onRevalidateCart]
  );

  const isGuest = !currentUser;
  const fingerprint = cartRowsFingerprint(cartRows);
  // A guest's cart is only knowable after the client has read localStorage.
  const cartIsKnown = !isGuest || cartHydrated;

  // The merge fires wherever the buyer happened to be, so its summary is held in the cart
  // duck and shown here once. Take a local copy and clear the duck, so a remount after
  // this one shows nothing.
  const [shownMergeSummary, setShownMergeSummary] = useState(null);
  useEffect(() => {
    if (mergeSummaryHasContent(mergeSummary)) {
      setShownMergeSummary(mergeSummary);
      onClearMergeSummary();
    }
  }, [mergeSummary, onClearMergeSummary]);

  useEffect(() => {
    if (typeof window === 'undefined' || !cartIsKnown || !destinationHydrated) {
      return;
    }
    revalidateWithDestination(cartRows);
    // `cartRows` is a fresh array on every read, so the fingerprint is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint, cartIsKnown, destKey, destinationHydrated, revalidateWithDestination]);

  const lastRevalidatedAt = useRef(Date.now());
  useEffect(() => {
    if (typeof window === 'undefined' || !cartIsKnown || !destinationHydrated) {
      return undefined;
    }
    const revalidateIfStale = () => {
      if (Date.now() - lastRevalidatedAt.current < REFOCUS_REVALIDATE_MS) {
        return;
      }
      lastRevalidatedAt.current = Date.now();
      revalidateWithDestination(cartRows);
    };
    window.addEventListener('focus', revalidateIfStale);
    return () => window.removeEventListener('focus', revalidateIfStale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint, cartIsKnown, destKey, destinationHydrated, revalidateWithDestination]);

  const [checkoutInProgress, setCheckoutInProgress] = useState(false);
  const [checkoutError, setCheckoutError] = useState(false);
  const onCheckout = useCallback(
    box => {
      if (checkoutInProgress) {
        return;
      }
      setCheckoutInProgress(true);
      setCheckoutError(false);

      prepareBoxCheckout({
        box,
        cartRows,
        revalidateCart: async rows => {
          const action = await revalidateWithDestination(rows);
          if (action?.error || action?.meta?.requestStatus === 'rejected') {
            throw action.payload || action.error || new Error('revalidate failed');
          }
          return action?.payload || action;
        },
        fetchListing: listingId => dispatch(fetchListingForCheckout(listingId, config)),
      })
        .then(result => {
          if (!result?.ok) {
            setCheckoutInProgress(false);
            return;
          }

          const listingId = result.listing.id?.uuid || result.listing.id;
          const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);
          const orderData =
            result.orderData.deliveryMethod === 'shipping' && destination
              ? { ...result.orderData, shippingDestination: destination }
              : result.orderData;
          dispatch(
            setInitialValues(
              {
                listing: result.listing,
                orderData,
                confirmPaymentError: null,
              },
              false
            )
          );
          dispatch(initializeCardPaymentData());
          history.push(
            createResourceLocatorString(
              'CheckoutPage',
              routes,
              {
                id: listingId,
                slug: createSlug(result.listing.attributes?.title || 'listing'),
              },
              {}
            )
          );
        })
        .catch(() => {
          setCheckoutInProgress(false);
          setCheckoutError(true);
        });
    },
    [
      cartRows,
      checkoutInProgress,
      config,
      destination,
      dispatch,
      history,
      revalidateWithDestination,
      routes,
    ]
  );

  const { boxes, unavailableRows } = useMemo(() => partitionCartRows({ rows, groups }), [
    rows,
    groups,
  ]);

  const watchedCount = getWatchedListingEntries(currentUser).length;
  const listingCount = Array.isArray(rows) ? rows.length : 0;
  const itemCount = cartItemCount(rows);
  const boxCount = boxes.length;
  const title = intl.formatMessage({ id: 'CartPage.title' });
  const signInHref = `${apiBaseUrl()}/api/auth/xenforo?from=${encodeURIComponent(CART_PATH)}`;

  // Emptiness is rows, never boxes: unlisted rows belong to no box, so a cart whose every
  // row went unlisted has rows and zero groups.
  const isEmpty = isCartEmpty(rows);
  // Persisted rows with no server answer yet are a cart we cannot describe, not an empty
  // one. Showing "Your cart is empty" over them would be the same lie as deleting them.
  const cartHasShippingRows = cartRows.some(row => row.deliveryMethod === 'shipping');
  const awaitingDestinationHydration =
    typeof window !== 'undefined' && cartHasShippingRows && !destinationHydrated;
  const quoteStale =
    cartRows.length > 0 && cartRowsFingerprint(cartRows) !== cartRowsFingerprint(rows);
  const awaitingFirstQuote =
    !revalidateError && (!loaded || quoteStale || (isEmpty && cartRows.length > 0));
  const isLoading = !cartIsKnown || awaitingDestinationHydration || awaitingFirstQuote;
  // A cart we failed to quote is unknown, not empty, so it gets the error and nothing else.
  const isUnknown = !!revalidateError && isEmpty;
  // Keep the dest picker visible while the first quote is in flight so a buyer
  // with no sticky dest can still choose a country/state.
  const showShippingDestination =
    destinationHydrated && cartHasShippingRows && cartRows.length > 0 && !isUnknown;

  const rowHandlers = {
    onRemove: onRemoveFromCart,
    onUpdateQty: onUpdateCartRowQty,
    onUpdateDeliveryMethod: onUpdateCartRowDeliveryMethod,
  };

  const content = isLoading ? (
    <p className={css.loading}>
      <FormattedMessage id="CartPage.loading" />
    </p>
  ) : isUnknown ? null : isEmpty ? (
    <EmptyCart watchedCount={watchedCount} />
  ) : (
    <>
      {shownMergeSummary ? (
        <MergeNotice summary={shownMergeSummary} onDismiss={() => setShownMergeSummary(null)} />
      ) : null}

      {isGuest ? (
        <div className={css.deviceNotice} role="status">
          <FormattedMessage id="CartPage.guestDeviceNotice" />
        </div>
      ) : null}

      {boxes.map(box => (
        <CartBoxCard
          key={box.key}
          box={box}
          isGuest={isGuest}
          signInHref={signInHref}
          conflictedListingIds={shownMergeSummary?.conflicts || []}
          updateInProgress={updateInProgress}
          updatingListingId={updatingListingId}
          onCheckout={onCheckout}
          checkoutInProgress={checkoutInProgress}
          {...rowHandlers}
        />
      ))}

      {unavailableRows.length > 0 ? (
        <section className={css.unavailableSection}>
          <H3 as="h2" className={css.unavailableHeading}>
            <FormattedMessage id="CartPage.noLongerAvailableHeading" />
          </H3>
          <ul className={css.unavailableRows}>
            {unavailableRows.map(row => (
              <CartRow key={row.listingId} row={row} {...rowHandlers} />
            ))}
          </ul>
        </section>
      ) : null}

      {checkoutError ? (
        <p className={css.error} role="alert">
          <FormattedMessage id="CartPage.checkoutStartError" />
        </p>
      ) : null}
    </>
  );

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer currentPage="CartPage" />}>
        <div className={css.root} aria-busy={revalidateInProgress || undefined}>
          {isEmpty && !showShippingDestination ? null : (
            <header className={css.pageHeader}>
              {isEmpty || isLoading ? null : (
                <>
                  <H3 as="h1" className={css.heading}>
                    <FormattedMessage id="CartPage.heading" />
                  </H3>
                  {listingCount > 0 ? (
                    <p className={css.byline}>
                      <FormattedMessage
                        id="CartPage.byline"
                        values={{ itemCount, listingCount, boxCount }}
                      />
                    </p>
                  ) : null}
                </>
              )}
              {showShippingDestination ? (
                <ShippingDestinationSelect
                  idPrefix="CartPage.shippingDestination"
                  className={css.shippingDestination}
                  destination={destination}
                  onChange={setDestination}
                />
              ) : null}
            </header>
          )}
          {revalidateError ? (
            <p className={css.error} role="alert">
              <FormattedMessage id="CartPage.revalidateError" />
            </p>
          ) : null}
          {content}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { rows, groups, loaded, revalidateInProgress, revalidateError } = state.CartPage;
  const { hydrated, updateInProgress, updatingListingId } = state.cart;

  return {
    currentUser,
    cartRows: cartRowsSelector(state),
    cartHydrated: hydrated,
    rows,
    groups,
    loaded,
    revalidateInProgress,
    revalidateError,
    mergeSummary: cartMergeSummarySelector(state),
    updateInProgress,
    updatingListingId,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onRevalidateCart: (rows, options) => dispatch(revalidateCart(rows, options)),
  onRemoveFromCart: listingId => dispatch(removeFromCart(listingId)),
  onUpdateCartRowQty: params => dispatch(updateCartRowQty(params)),
  onUpdateCartRowDeliveryMethod: params => dispatch(updateCartRowDeliveryMethod(params)),
  onClearMergeSummary: () => dispatch(clearMergeSummary()),
});

const CartPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(CartPageComponent);

export default CartPage;
